import { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from '../supabase';
import { getChatRequestUrl } from '../config';
import { GUEST_PROFILE_KEY } from '../components/FirstTimeOverview';

/**
 * Move a climate answered as a guest onto a real account.
 *
 * The climate modal saves before it asks about an account, deliberately, so an
 * answer survives someone who never signs up. Getting it onto the account is a
 * separate job, and nothing else does it: the only other path lives in
 * PersonalProfile, which fires solely when that screen is opened, skips accounts
 * that already hold a climate, and never carries water_type. Someone arriving
 * from the climate email lands in chat and hits none of it, which broke the
 * modal's own promise, "sign in and this stays on your profile".
 *
 * Both ways in need it, for different reasons. Signing in to an existing account
 * swaps the user id, so the answer and the account share no key at all.
 * Registering keeps the id, but convertToEmailUser writes only first_name and
 * marketing_opt_in, so the climate would still sit unread in localStorage.
 *
 * The newer answer wins over whatever is on file. They just told us, now.
 * Never throws: a failed migration must not fail an authentication.
 */
const migrateGuestClimate = async (userId) => {
  if (!userId) return;
  try {
    const guest = JSON.parse(localStorage.getItem(GUEST_PROFILE_KEY) || '{}');
    const climate = Array.isArray(guest.climate) ? guest.climate : null;
    if (!climate || climate.length === 0) return;

    const { error } = await supabase.from('user_profiles').upsert(
      {
        user_id: userId,
        climate,
        water_type: guest.water_type || null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' }
    );
    if (error) throw error;

    // Drop only what was just migrated. The rest of the guest profile is still
    // wanted by PersonalProfile's own migration. Leaving the climate behind
    // would let it reapply to a different account if someone else signs in on
    // this device.
    const { climate: _c, water_type: _w, ...rest } = guest;
    localStorage.setItem(GUEST_PROFILE_KEY, JSON.stringify(rest));
  } catch (e) {
    console.warn('Guest climate migration skipped:', e);
  }
};

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [needsPasswordReset, setNeedsPasswordReset] = useState(false);

  useEffect(() => {
    // Get initial session
    const getSession = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;

        if (session?.user) {
          setUser(session.user);
          setIsAnonymous(session.user.is_anonymous ?? false);
        } else {
          // No session - auto-create anonymous user
          const { data, error } = await supabase.auth.signInAnonymously();
          if (error) throw error;
          setUser(data.user);
          setIsAnonymous(true);
        }
      } catch (error) {
        console.warn('Could not start a Son:mi session:', error);
        setUser(null);
        setIsAnonymous(false);
      } finally {
        setLoading(false);
      }
    };

    getSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          // User clicked the reset link in their email — show the set-new-password form
          setUser(session?.user ?? null);
          setIsAnonymous(false);
          setNeedsPasswordReset(true);
          setLoading(false);
          return;
        }
        if (event === 'SIGNED_OUT') {
          // Re-initialize as anonymous user so the app always has a valid user state
          setLoading(true);
          try {
            const { data, error } = await supabase.auth.signInAnonymously();
            if (error) throw error;
            setUser(data.user);
            setIsAnonymous(true);
          } catch (error) {
            console.warn('Could not restart a Son:mi session:', error);
            setUser(null);
            setIsAnonymous(false);
          } finally {
            setLoading(false);
          }
          return;
        }
        setUser(session?.user ?? null);
        setIsAnonymous(session?.user?.is_anonymous ?? false);
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    // Capture the GUEST token before signing in. Registering keeps your user id
    // (updateUser on the anonymous session), so history follows automatically.
    // Signing in to an existing account swaps the id, which orphaned everything
    // said as a guest. We hand the backend the guest's own token as proof of
    // ownership; the id alone would let anyone claim any conversation.
    let guestToken = null;
    try {
      const { data: pre } = await supabase.auth.getSession();
      if (pre?.session?.user?.is_anonymous) guestToken = pre.session.access_token;
    } catch (_e) {}

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (!error && guestToken && data?.session?.access_token) {
      try {
        await fetch(getChatRequestUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${data.session.access_token}`,
          },
          body: JSON.stringify({ migrateGuestConversation: true, guestToken }),
        });
      } catch (mErr) {
        // Never block sign-in on this; worst case the guest turns stay behind.
        console.warn('Guest history migration skipped:', mErr);
      }
    }

    // Gated on guestToken, not just on a successful sign-in. That token is the
    // proof this sign-in came from a guest session, so the answer in localStorage
    // belongs to the person signing in. Without it, a stale answer left by
    // someone else on a shared device would land on the next account used here.
    if (!error && guestToken) await migrateGuestClimate(data?.user?.id);

    return { data, error };
  };

  const signUp = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const resetPassword = async (email) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    return { data, error };
  };

  const updatePassword = async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    if (!error) setNeedsPasswordReset(false);
    return { data, error };
  };

  // Sign in anonymously (used if auto-sign-in fails)
  const signInAnonymously = async () => {
    const { data, error } = await supabase.auth.signInAnonymously();
    if (!error && data.user) {
      setUser(data.user);
      setIsAnonymous(true);
    }
    return { data, error };
  };

  // Convert anonymous user to email/password user
  const convertToEmailUser = async (email, password, firstName, marketingOptIn = true) => {
    const updatePayload = { email, password };
    if (firstName) updatePayload.data = { first_name: firstName };
    const { data, error } = await supabase.auth.updateUser(updatePayload);
    if (!error && data?.user?.id) {
      setIsAnonymous(false);
      await supabase.from('user_profiles').upsert(
        {
          user_id: data.user.id,
          first_name: firstName || null,
          marketing_opt_in: marketingOptIn,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id' }
      );
      await migrateGuestClimate(data.user.id);
    }
    return { data, error };
  };

  // Helper function to get auth session (replaces fetchAuthSession)
  const getAuthSession = async () => {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    return {
      tokens: {
        accessToken: session?.access_token,
        refreshToken: session?.refresh_token,
      },
      userId: session?.user?.id
    };
  };

  const value = {
    user,
    loading,
    isAnonymous,
    needsPasswordReset,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    getAuthSession,
    signInAnonymously,
    convertToEmailUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
